import axios from 'axios';
import { logger } from './loggingService';

// Set API base URL from environment variable. Falls back to '/api' (same origin,
// proxied to the backend by nginx / the Vite dev server) — matching services/api.js
// so both clients behave the same when VITE_API_BASE_URL is unset.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Configure API client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 30000,
  withCredentials: true
});

// Add request interceptor to log request details and handle errors
apiClient.interceptors.request.use(
  (config: any) => {
    // Add timestamp to request for debugging
    config.metadata = { startTime: new Date() };
    
    // Add Authorization token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log the request details
    logger.debug('Making API request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      data: config.data,
      params: config.params
    });
    
    return config;
  },
  error => {
    logger.error('Request configuration error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: any) => {
    // Calculate request duration
    const meta = (response.config as any).metadata;
    const duration = meta ? Date.now() - new Date(meta.startTime).getTime() : 0;

    // Log response with duration
    logger.debug('API Response received', {
      status: response.status,
      statusText: response.statusText,
      duration: `${duration}ms`,
      data: response.data
    });

    return response;
  },
  error => {
    logger.error('API Error:', {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      config: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        data: error.config?.data
      }
    });

    // Build an Error that carries a friendly message BUT still exposes the
    // original axios response, so per-request handlers can inspect
    // error.response.status / error.response.data for more detailed messages.
    const buildError = (message: string) => {
      const err: any = new Error(message);
      if (error.response) {
        err.response = error.response;
        err.status = error.response.status;
      }
      if (error.code) {
        err.code = error.code;
      }
      return err;
    };

    // Handle specific error cases
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      // If we are not already on login, redirect
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw buildError('Session expired. Please log in again.');
    } else if (error.code === 'ECONNABORTED') {
      throw buildError('Request timed out. Please check your network connection and try again.');
    } else if (!error.response) {
      throw buildError('Network error. Please check your connection and make sure the server is running.');
    } else {
      const errorMessage = error.response.data?.detail || error.response.data?.message || 'Request failed';
      throw buildError(errorMessage);
    }
  }
);

export const cryopreservationService = {
  // Helper function to check server status
  checkServerStatus: async () => {
    try {
      const response = await apiClient.get('/debug/status');
      return response.data;
    } catch (error) {
      return { error: error.message || 'Failed to check server status' };
    }
  },
  
  // Helper function to format data for API requests
  _formatCryoData: (data) => {
    // Helper function to safely parse a number
    const safeParseFloat = (value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };
    
    // Clean and parse data to match server expectations
    const formatted = {
      cell_line_name: data.cell_line_name?.trim() || '',
      origin: data.origin?.toString() || null,
      passage_number: safeParseFloat(data.passage_number),
      frozen_by: data.frozen_by?.trim() || null,
      number_of_cells_frozen: safeParseFloat(data.number_of_cells_frozen),
      number_of_vials_frozen: safeParseFloat(data.number_of_vials_frozen) || 0,
      storage_location: 'Liquid Nitrogen',
      date_of_cryo: data.date_of_cryo || null,
      time_of_freezing: data.time_of_freezing || null,
      box_number: data.box_number !== null && data.box_number !== undefined && data.box_number !== '' ? data.box_number.toString() : null,
      position_in_box: data.position_in_box !== null && data.position_in_box !== undefined && data.position_in_box !== '' ? data.position_in_box.toString() : null,
      notes: data.notes?.trim() || '',
      revival_history: "[]" // String representation of empty array
    };
    
    return formatted;
  },
  
  getAllCryopreservations: async (page = 1, limit = 25, searchTerm = '', selectedOrigin = '', selectedFrozenBy = '', selectedBoxNumber = '', selectedPositionInBox = '', selectedTimeOfFreezing = '', orderBy = 'date_of_cryo', order = 'desc') => {
    try {
      const response = await apiClient.get('/cryopreservation', {
        params: {
          page: page,
          limit: limit,
          search_term: searchTerm,
          origin: selectedOrigin,
          frozen_by: selectedFrozenBy,
          boxNumber: selectedBoxNumber,
          positionInBox: selectedPositionInBox,
          timeOfFreezing: selectedTimeOfFreezing,
          order_by: orderBy,
          order: order
        }
      });
      
      if (!response.data || !Array.isArray(response.data.items) || typeof response.data.total !== 'number') {
        throw new Error('Invalid response format from server');
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  getFilterOptions: async () => {
    try {
      const response = await apiClient.get('/cryopreservation/filter-options');
      
      if (!response.data || !response.data.filterOptions) {
        throw new Error('Invalid response format from server');
      }
      
      return response.data.filterOptions;
    } catch (error) {
      throw error;
    }
  },

  searchCryopreservations: async (term = '', page = 1, limit = 25, selectedOrigin = '', selectedFrozenBy = '', selectedBoxNumber = '', selectedPositionInBox = '', selectedTimeOfFreezing = '', orderBy = 'date_of_cryo', order = 'desc') => {
    try {
      const searchParams = {
        search_term: term,
        page: page,
        limit: limit,
        origin: selectedOrigin,
        frozen_by: selectedFrozenBy,
        boxNumber: selectedBoxNumber,
        positionInBox: selectedPositionInBox,
        timeOfFreezing: selectedTimeOfFreezing,
        order_by: orderBy,
        order: order
      };
      
      const response = await apiClient.get('/cryopreservation/search', { params: searchParams });
      
      if (!response.data || !response.data.items || typeof response.data.total !== 'number') {
        throw new Error('Invalid response format from server');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error searching cryopreservations:', error);
      throw error;
    }
  },

  addCryopreservation: async (data) => {
    try {
      // Use the helper function to format data
      const formattedData = cryopreservationService._formatCryoData(data);

      const response = await apiClient.post('/cryopreservation/add', formattedData);
      return response.data;
    } catch (error) {
      if (error.response) {
        let errorMessage;
        const detail = error.response.data?.detail;
        
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            if (typeof err === 'object') {
              if (err.loc && err.loc.length > 1) {
                const fieldName = err.loc[1];
                return `${fieldName}: ${err.msg}`;
              }
              return err.msg;
            }
            return err;
          }).join('\n');
        } else {
          errorMessage = detail || error.response.data?.message || 'Failed to add cryopreservation';
        }
        throw new Error(errorMessage);
      }
      throw error;
    }
  },

  updateCryopreservation: async (id, data) => {
    try {
      // Format the data according to the backend model
      const formattedData = {
        cell_line_name: data.cell_line_name?.trim() || '',
        origin: data.origin === 'NaN' ? null : data.origin?.toString() || null,
        passage_number: data.passage_number === 'NaN' ? null : data.passage_number?.toString().trim() || null,
        frozen_by: data.frozen_by === 'NaN' ? null : data.frozen_by?.trim() || null,
        number_of_cells_frozen: data.number_of_cells_frozen === 'NaN' ? null : 
          (data.number_of_cells_frozen ? Number(data.number_of_cells_frozen) : null),
        number_of_vials_frozen: data.number_of_vials_frozen === 'NaN' ? null : 
          (data.number_of_vials_frozen ? Number(data.number_of_vials_frozen) : null),
        storage_location: 'Liquid Nitrogen',
        box_number: data.box_number === 'NaN' ? null : 
          (data.box_number !== null && data.box_number !== undefined && data.box_number !== '' ? data.box_number.toString() : null),
        position_in_box: data.position_in_box === 'NaN' ? null : 
          (data.position_in_box !== null && data.position_in_box !== undefined && data.position_in_box !== '' ? data.position_in_box.toString() : null),
        date_of_cryo: data.date_of_cryo === 'NaN' ? null : data.date_of_cryo || null,
        time_of_freezing: data.time_of_freezing === 'NaN' ? null : data.time_of_freezing || null,
        notes: data.notes === 'NaN' ? '' : data.notes?.trim() || '',
        revival_history: data.revival_history || "[]",
        reason_for_change: data.reason_for_change?.trim() || '',
      };

      const response = await apiClient.put(`/cryopreservation/update/${id}`, formattedData);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to update cryopreservation');
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error(error.message || 'Failed to update cryopreservation');
      }
    }
  },

  deleteCryopreservation: async (id, reason_for_change, password) => {
    try {
      const cleanId = id.trim();
      const headers = {};
      if (password) {
        headers['X-Signature-Password'] = password;
      }
      const response = await apiClient.delete(`/cryopreservation/delete/${cleanId}`, {
        params: { reason_for_change },
        headers
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        // Return detailed error from backend if available
        throw new Error(error.response.data.detail || error.response.data.message || 'Failed to delete cryopreservation');
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error(error.message || 'Failed to delete cryopreservation');
      }
    }
  },

  getPendingDeletions: async () => {
    try {
      const response = await apiClient.get('/cryopreservation/pending-deletions');
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.detail || error.response.data.message || 'Failed to fetch pending deletions');
      } else {
        throw new Error(error.message || 'Failed to fetch pending deletions');
      }
    }
  },

  approveDeletion: async (id, password) => {
    try {
      const cleanId = id.trim();
      const headers = {};
      if (password) {
        headers['X-Signature-Password'] = password;
      }
      const response = await apiClient.post(`/cryopreservation/approve-delete/${cleanId}`, {}, { headers });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.detail || error.response.data.message || 'Failed to approve deletion');
      } else {
        throw new Error(error.message || 'Failed to approve deletion');
      }
    }
  },

  rejectDeletion: async (id) => {
    try {
      const cleanId = id.trim();
      const response = await apiClient.post(`/cryopreservation/reject-delete/${cleanId}`);
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.detail || error.response.data.message || 'Failed to reject deletion');
      } else {
        throw new Error(error.message || 'Failed to reject deletion');
      }
    }
  },

  reviveCryopreservation: async (id, revivalDetails) => {
    try {
      if (!id) {
        throw new Error('Missing cryopreservation ID');
      }
      
      const positions = revivalDetails?.positions_to_revive;
      const hasPositions = Array.isArray(positions) ? positions.length > 0 : !!positions;
      if (!revivalDetails || !revivalDetails.revived_by || (!hasPositions && !revivalDetails.vials_to_revive)) {
        throw new Error('Missing required revival details');
      }

      const cleanId = id.toString().trim();

      // Format revival details to ensure correct data types. Position-based
      // revival (operator picked specific slots) takes precedence; the backend
      // derives the vial count from the positions.
      const formattedRevivalDetails: Record<string, unknown> = {
        revived_by: revivalDetails.revived_by?.trim(),
        notes: revivalDetails.notes?.trim() || '',
        date: new Date().toISOString(),
      };
      if (hasPositions) {
        formattedRevivalDetails.positions_to_revive = Array.isArray(positions)
          ? positions.join(',')
          : positions;
      } else {
        formattedRevivalDetails.vials_to_revive = parseInt(revivalDetails.vials_to_revive, 10);
      }

      const response = await apiClient.post(`/cryopreservation/revive/${cleanId}`, formattedRevivalDetails);
      return response.data;
    } catch (error) {
      if (error.response) {
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        let errorMessage;
        if (statusCode === 400) {
          errorMessage = errorData?.message || errorData?.errors?.[0] || 'Cannot revive more vials than currently frozen';
        } else if (statusCode === 422) {
          errorMessage = errorData?.message || errorData?.errors?.[0] || 'Invalid revival details';
        } else if (statusCode === 404) {
          errorMessage = 'This cryopreservation record was not found. It may have been deleted or does not exist.';
        } else {
          errorMessage = errorData?.message || 'Failed to revive cryopreservation';
        }
        
        throw new Error(errorMessage);
      }
      
      throw error;
    }
  },

  exportToExcel: async (filters = {}) => {
    try {
      const requestFilters = {
        ...filters,
        dateFormat: 'date-only'
      };
      
      const response = await apiClient.get('/cryopreservation/export', {
        params: requestFilters,
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      // Verify the content type before proceeding
      const contentType = String(response.headers['content-type'] || '');
      if (contentType && !contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        console.error('Unexpected content type:', contentType);
        throw new Error('Server did not return an Excel file. Please try again.');
      }
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cryopreservations.xlsx';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Export error:', error);
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to export data');
      } else {
        throw new Error(error.message || 'Failed to export data');
      }
    }
  },

  batchUploadCryopreservation: async (formData) => {
    try {
      // Process form data before sending
      const processedFormData = new FormData();
      
      // Copy all files
      for (const file of formData.getAll('files')) {
        processedFormData.append('files', file);
      }
      
      // Add other form data
      processedFormData.append('delimiter', formData.get('delimiter') || ',');
      processedFormData.append('has_header', formData.get('has_header') || 'true');
      
      // Create data structure based on database schema
      const dataStructure = {
        columns: {
          'Cell Line Name': 'cell_line_name',
          'Number of Vials': 'number_of_vials_frozen',
          'Box Number': 'box_number',
          'Position in Box': 'position_in_box',
          'Time of Freezing': 'time_of_freezing'
        },
        defaults: {
          cell_line_name: '',
          origin: null,
          passage_number: null,
          frozen_by: null,
          number_of_cells_frozen: null,
          number_of_vials_frozen: null,
          storage_location: 'Liquid Nitrogen',
          box_number: '',
          position_in_box: '',
          time_of_freezing: null,
          notes: null,
          date_of_cryo: null,
          revival_history: []
        }
      };
      
      processedFormData.append('column_mapping', formData.get('column_mapping') || JSON.stringify(dataStructure.columns));
      processedFormData.append('data_structure', JSON.stringify(dataStructure));

      const response = await apiClient.post('/cryopreservation/batch-upload', processedFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to upload files');
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error(error.message || 'Failed to upload files');
      }
    }
  },

  getBoxOccupancy: async (boxNumber: string | number, excludeRecordId?: string) => {
    try {
      const params: Record<string, string> = { box_number: String(boxNumber) };
      if (excludeRecordId) params.exclude_record_id = excludeRecordId;
      const response = await apiClient.get('/cryopreservation/box-occupancy', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRemainingBoxSpace: async () => {
    try {
      const response = await apiClient.get('/cryopreservation/remaining-space');
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid response format for remaining space');
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStatistics: async () => {
    try {
      const response = await apiClient.get('/cryopreservation/stats');
      if (!response.data || !response.data.summary) {
        throw new Error('Invalid response format for statistics');
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};